import type { ReactNode } from 'react'
import { AlertTriangle, Inbox } from 'lucide-react'
import { Button } from './Button'
import { cn } from '@/lib/cn'

/** Every list needs one. Icon + one-line explanation + a way forward. */
export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)}>
      <span className="text-ink-faint" aria-hidden>
        {icon ?? <Inbox size={22} />}
      </span>
      <p className="font-head text-[14px] font-semibold text-ink">{title}</p>
      {description && (
        <p className="max-w-[46ch] text-[13px] leading-5 text-ink-muted">{description}</p>
      )}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  )
}

/**
 * Never a bare "Something went wrong". The message says what failed and the
 * retry keeps the shell interactive (docs/02-app-shell.md §6).
 */
export function ErrorState({
  title = 'That did not load',
  message,
  onRetry,
  className,
}: {
  title?: string
  message: string
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}
    >
      <AlertTriangle size={22} className="text-brand-red" aria-hidden />
      <p className="font-head text-[14px] font-semibold text-ink">{title}</p>
      <p className="max-w-[52ch] text-[13px] leading-5 text-ink-muted">{message}</p>
      {onRetry && (
        <Button size="sm" className="mt-1.5" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('animate-pulse rounded-[4px] bg-surface-sunk', className)}
    />
  )
}

/** Table-shaped placeholder. Matching the real layout beats a spinner. */
export function SkeletonRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2 p-4" aria-label="Loading" role="status">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-5', c === 0 ? 'flex-[2]' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  )
}
